import type { IStorageAdapter } from "../utils";
import type { IAssistant } from "./assistant";
import type { IMessageInfo } from "./generated-video";
import type { IGenerationFailureErrorMetadata } from "./generation-failure";
import type { IModel } from "./models";
import type { IPiiSpan } from "./pii-span";
import type { IPresentationThemePalette } from "./presentation-theme";
import type { TSkillScriptRunRecord } from "./skill-sandbox";
import type { TSkillActivationRecord } from "./skills";
import type { ITokenBudgetErrorMetadata } from "./token-budget";

// NOTE: NSAI-5678 — which side of the assistant / regular split a chat-list
// request asks for. Absent means every chat, which is what folders, archive and
// search want and what an older client keeps sending.
export enum EChatListKind {
  REGULAR = "regular",
  ASSISTANT = "assistant",
}

// NOTE: NSAI-5678 — the assistant meta the sidebar needs to file an assistant
// chat under its assistant and label the group. Resolved server-side so a chat
// whose assistant has since been unshared or deleted still shows its name. No
// avatar on purpose — those are data URLs of up to 512KB and would repeat on
// every row; the clients look them up in the assistants cache they already
// hold and fall back to the generic assistant glyph.
export interface IChatListAssistant {
  uuid: string;
  name: string;
}

export interface IChatListObject {
  uuid: string;
  title: string;
  isArchived: boolean;
  isPinned: boolean;
  pinnedAt: string | null;
  folderId: string | null;
  createdAt: string;
  // NOTE: NSAI-4559 — latest message timestamp; the sidebar sorts and
  // date-groups by this instead of createdAt. Optional so a client running
  // against an older server still renders (grouping falls back to createdAt).
  lastMessageAt?: string | null;
  telegramChatId: number | null;
  teamsConversationId: string | null;
  slackChannelId: string | null;
  // NOTE: Boolean flag only — the server deliberately never sends the WhatsApp
  // phone number on chat payloads (it is directly-identifying PII).
  isWhatsappChat: boolean;
  // NOTE: NSAI-4950 — boolean flag only; the Matrix room id stays server-side
  // and the clients only need to know the chat is read-only here.
  isMatrixChat: boolean;
  hasActiveShare: boolean;
  // NOTE: NSAI-5678 — the chat's current assistants in slot order; empty for a
  // model chat. Optional so a client against an older server still renders
  // (every chat then counts as a regular one).
  assistants?: IChatListAssistant[];
}

export interface IGetChatListParams {
  limit: number;
  offset: number;
  search?: string;
  folderId?: string;
  // NOTE: NSAI-5678 — omit to list every chat regardless of kind.
  kind?: EChatListKind;
}

export interface ISelectPinnedChatsProps {
  chats: IChatListObject[];
}

export enum ESearchMode {
  DEEP = "deep",
  WEB = "web",
  NONE = "none",
}

export enum EReasoningMode {
  THINKING = "thinking",
  FAST = "fast",
}

export type TReasoningLabelKey =
  | "input.reasoning_thinking"
  | "input.reasoning_fast";

export interface IReasoningModeOption {
  mode: EReasoningMode;
  labelKey: TReasoningLabelKey;
}

export interface IReasoningItem {
  isReasoningModel: boolean;
}

export interface IIsReasoningApplicableProps {
  selectedModels: IReasoningItem[];
  selectedAssistants: IReasoningItem[];
}

export enum EMessageRole {
  SYSTEM = "system",
  SYSTEM_ERROR = "system_error",
  USER = "user",
  ASSISTANT = "assistant",
}

export enum EGenerationType {
  Image = "image",
  // NOTE: NSAI-6621 — a turn answered by a video-generation model.
  Video = "video",
  Text = "text",
  Summary = "summary",
  BranchContext = "branch_context",
}

export enum EToolApprovalState {
  REQUESTED = "requested",
  APPROVED = "approved",
  DENIED = "denied",
}

export enum ESelectionKind {
  MODELS = "models",
  ASSISTANTS = "assistants",
}

export enum EModelChangeKind {
  SWITCHED_TO = "switched_to",
  SWITCHED_BACK = "switched_back",
  CHANGED = "changed",
  ADDED = "added",
  REMOVED = "removed",
  UPDATED = "updated",
  CONTINUING_WITH = "continuing_with",
}

export interface IIndexedSelectionItem {
  uuid: string;
  index: number;
}

export interface IIndexedSelectionItemModel extends IIndexedSelectionItem {
  name: string;
  title: string;
  provider: string;
  hostingProvider: string;
}

export interface IIndexedSelectionItemAssistant extends IIndexedSelectionItem {
  name: string;
  avatar: string | null;
  isDeleted?: boolean;
}

export interface IAssistantMeta {
  name: string;
  avatar: string | null;
  isDeleted?: boolean;
}
export enum EKnowledgeSourceType {
  KNOWLEDGE = "knowledge",
  CHAT_FILE = "chat_file",
  ASSISTANT_FILE = "assistant_file",
}

// NOTE: Cross-chat context provenance (NSAI-3501) — a prior chat whose
// user-memory facts contributed context to the current answer. Surfaced under
// the assistant message so the user can trace an injected value and open the
// source chat.
export interface IContextChatSource {
  chatUuid: string;
  title: string | null;
  // NOTE: ISO date (no time) of the source chat, for a stable, locale-free label.
  matchedAt: string;
  // NOTE: Recalled fact text (NSAI-3501) — used to match the answer's actual
  // value to the chat that declared it, so only the chat the answer was based on
  // is shown. Not rendered directly.
  content?: string;
}

export enum EMessageFileSource {
  USER_UPLOAD = "user_upload",
  TOOL_OUTPUT = "tool_output",
  AI_GENERATED = "ai_generated",
  ASSISTANT = "assistant",
}

// NOTE: Represents a file attachment on a message returned from the chat history API
export interface IMessageFileAttachment {
  uuid: string;
  name: string;
  type: string;
  source?: EMessageFileSource;
  // NOTE: Base64 data URL for image files (e.g. "data:image/png;base64,...")
  imageBase64?: string | null;
}

export interface IMessageError {
  content: string;
  code?: string;
  tokenBudget?: ITokenBudgetErrorMetadata;
  // NOTE: NSAI-5810 — present only when `code` is GENERATION_FAILURE: the turn
  // failed because our own code faulted, and this carries what the failure card
  // needs that the content cannot (retryability, and the engine wording that
  // belongs in a collapsed disclosure rather than in the sentence the user
  // reads).
  generationFailure?: IGenerationFailureErrorMetadata;
}

export interface IToolFile {
  name: string;
  type: string;
  size?: number;
  // NOTE: File UUID from message_files table — used by download API.
  uuid?: string;
}

// NOTE: NSAI-5656 — one HTML page the assistant produced, as the preview panel
// lists it: a version the user can switch to, download or open standalone.
export interface IHtmlPreviewVersion {
  fileUuid: string;
  fileName: string;
  messageUuid: string;
  createdAt: string;
}

export interface IAreHtmlPreviewVersionsEqualProps {
  first: IHtmlPreviewVersion[];
  second: IHtmlPreviewVersion[];
}

export interface IMessageDeltaPayload {
  uuid: string;
  delta: string;
  chatUuid?: string;
}

export interface IMessageContentUpdatePayload {
  uuid: string;
  content: string;
  chatUuid?: string;
}

export interface IToolCallPayload {
  uuid: string;
  toolName: string;
  args: Record<string, unknown>;
  chatUuid?: string;
}

export interface IToolApprovalRequestPayload {
  uuid: string;
  approvalId: string;
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  chatUuid?: string;
}

export interface IToolApprovalRespondedPayload {
  uuid: string;
  approvalId: string;
  approved: boolean;
  reason?: string;
  chatUuid?: string;
}

export interface IToolFilePayload {
  uuid: string;
  chatUuid?: string;
  toolName: string;
  file: IToolFile;
}

// NOTE: Emitted when a tool-generated file (e.g. a presentation deck) fails to
// upload to S3 after retries, so no `toolFile` will follow. Clients surface this
// to the user so a failed attachment isn't silent (NSAI-3622 / NSAI-3613).
export interface IToolFileErrorPayload {
  uuid: string;
  chatUuid?: string;
  toolName: string;
  file: Pick<IToolFile, "name" | "type">;
}

export interface IReasoningStepPayload {
  uuid: string;
  reasoning: string;
  chatUuid?: string;
}

export interface IStreamErrorPayload {
  uuid: string;
  error: string;
  chatUuid?: string;
}

export interface IStreamFinishPayload {
  uuid: string;
  reason: string;
  chatUuid?: string;
}

// NOTE: NSAI-2871 — one retrieved source as the server numbered it for the run:
// `n` is the citation number the answer's inline `[n]` and its Sources section
// use, so the card can show which page each citation points at.
export interface ISearchQuerySource {
  n: number;
  url: string;
  title: string;
}

export interface IDeepSearchProgressPayload {
  uuid: string;
  status: string;
  message: string;
  query?: string;
  queryIndex?: number;
  totalQueries?: number;
  completedCount?: number;
  chatUuid?: string;
}

export interface IWebSearchProgressPayload {
  uuid: string;
  status: string;
  message: string;
  query?: string;
  queryIndex?: number;
  totalQueries?: number;
  completedCount?: number;
  chatUuid?: string;
}

export interface IMessagePreparationProgressPayload {
  uuid: string;
  status: string;
  message: string;
  chatUuid?: string;
}

export interface IModelForHistory {
  uuid: string;
  title: string;
  // NOTE: Optional region/hosting info used by UI for data-sovereignty indicators
  // (e.g. the moderation fallback badge). Not populated on legacy message payloads.
  region?: string;
  hostingProvider?: string;
}

export interface IMessage {
  uuid: string;
  chatUuid: string;
  role: EMessageRole;
  groupId?: string;
  slotIndex?: number;
  content: string;
  files: IMessageFileAttachment[];
  createdAt: string;
  updatedAt: string;
  parentId?: string | null;
  childrenIds?: string[] | null;
  model: IModelForHistory;
  provider: string;
  done: boolean;
  generationType: EGenerationType;
  error?: IMessageError | null;
  tool?: IMessageToolCall;
  toolApprovals?: Array<{
    tool: {
      name: string;
      args: Record<string, unknown>;
    };
    toolApproval: {
      approvalId: string;
      toolCallId: string;
      state: EToolApprovalState;
      approved?: boolean;
      reason?: string;
    };
  }>;
  toolFiles?: IToolFile[];
  reasoning?: string;
  // NOTE: User-selected reasoning mode at send time (thinking | fast). Only set
  // on input (user) messages sent with a reasoning-capable model; null/undefined
  // otherwise. The web app derives a chat's active reasoning mode from its
  // latest input message so the selector restores on revisit/reload, and the
  // analytics message-details modal resolves an AI response's mode from its
  // parent user message.
  reasoningMode?: EReasoningMode | null;
  // NOTE: NSAI-3308 — set on an INPUT (user) message only. It is the id the
  // transport generated for the send that produced this row, and it is what lets
  // a composer clear its pinned skills on CONFIRMED PERSISTENCE rather than on a
  // socket acknowledgement, which only proves the gateway received the payload.
  clientRequestId?: string | null;
  // NOTE: The server-normalized skills the user pinned to this user message. A
  // manual resend replays these, not whatever the composer holds now.
  requestedSkillUuids?: string[] | null;
  // NOTE: NSAI-5925 — set on an INPUT (user) message only: the skills this message
  // ASKED FOR and did not get, because it was already at the per-message cap. Its
  // sibling above records what was applied; without this one, reading the
  // conversation back gives no way to tell that a sixth skill was requested at all
  // — the `/name` is still in the body, but a mention in body text is
  // indistinguishable from ordinary prose.
  refusedSkillNames?: string[] | null;
  // NOTE: Server-owned activation records for an assistant message. Durable, so
  // the same timeline survives a refresh; the live `skillActivations` event
  // carries the same records for the same message uuid.
  skillActivations?: TSkillActivationRecord[];
  // NOTE: NSAI-3304 — server-owned sandboxed script-run records for an assistant
  // message. Durable, so the same block survives a refresh; the live
  // `skillScriptRun` event carries the same record for the same run uuid.
  skillScriptRuns?: TSkillScriptRunRecord[];
  // NOTE: NSAI-6156 — set ONLY by the reviewer-facing reads (Security Analytics
  // message context / thread window, the admin transcript) on a project that
  // anonymizes PII, and only for a message that actually carried some. Each
  // entry locates a value inside THIS message's `content` and names the token
  // the model received in its place, so the reviewer can see what was masked
  // rather than reading a restored body that looks untouched. The chat
  // transcript itself never sends it, which is why the chat render path is
  // unaffected.
  piiSpans?: IPiiSpan[] | null;
  streamError?: string;
  streamFinished?: boolean;
  deepSearchQueries?: Array<{
    query: string;
    status: string;
    sources?: ISearchQuerySource[];
    // NOTE: NSAI-2873 — the research round of a deep run's later topic;
    // absent for the planned queries (round one).
    round?: number;
  }>;
  deepSearchProgress?: {
    status: string;
    message: string;
    query?: string;
    queryIndex?: number;
    totalQueries?: number;
    completedCount?: number;
    round?: number;
  };
  webSearchQueries?: Array<{
    query: string;
    status: string;
    urls?: string[];
    sources?: ISearchQuerySource[];
    results?: Array<{
      title: string;
      url: string;
      favicon?: string;
    }>;
  }>;
  webSearchProgress?: {
    status: string;
    message: string;
    query?: string;
    queryIndex?: number;
    totalQueries?: number;
    completedCount?: number;
  };
  knowledgeSources?: Array<{
    // NOTE: content is only present during live streaming (websocket).
    // After page refresh, sources are loaded from DB which omits content.
    content?: string;
    score: number;
    fileName: string;
    fileUuid: string;
    // NOTE: Identifies the origin so the correct download API is used.
    // "knowledge" → knowledge base, "chat_file" / "assistant_file" → message_files.
    source?: EKnowledgeSourceType;
  }>;
  // NOTE: Cross-chat context provenance (NSAI-3501) — prior chats whose
  // user-memory facts fed this answer, so the UI can show WHICH chat an
  // injected value came from and link back to it.
  contextChatSources?: IContextChatSource[];
  // NOTE: True when the assistant response was produced by the probe-rerouting
  // fallback model (user prompt failed probe validation, organization opted for
  // rerouting instead of blocking). UI renders a "moderation fallback" badge.
  isReroutedByProbe?: boolean;
  // NOTE: True when the assistant response was produced by the org's PII
  // trusted model (entities were the subject of the query; anonymization is
  // off for this chat). UI renders a "trusted model" badge.
  isRoutedByPiiIntent?: boolean;
  // NOTE: NSAI-5876 — true when the assistant response ran on the model picked
  // by presentation best-model routing (NSAI-4157) instead of the user's own
  // selection. Read by the model-change informer, which narrates the user's
  // selection changes and must ignore turns the server routed.
  isRoutedForPresentation?: boolean;
  preparationProgress?: {
    status: string;
    message: string;
  };
  // NOTE: NSAI-5681 — "connect this app" cards raised on an assistant reply by
  // the server's requestIntegrationConnection tool. Durable (the history read
  // returns them) and live (the `integrationConnectPrompt` socket event carries
  // one at a time for the same message uuid), so the clients merge by provider
  // instead of appending.
  integrationConnectPrompts?: IIntegrationConnectPrompt[];
  // NOTE: NSAI-6925 — server-owned notes on how this assistant message was
  // produced (e.g. a video length the model could not render was adjusted).
  info?: IMessageInfo | null;
}

// NOTE: NSAI-5681 — one "connect this app" prompt on an assistant message.
// `provider` is an EIntegrationProvider slug the deployment can connect, and it
// is the whole record: WHICH account gets connected is decided on the
// provider's own OAuth screen behind the card's button, so no account detail is
// collected, carried or rendered. Rows written before that simplification may
// still hold an extra `accountHint` key in jsonb; nothing reads it.
// NOTE: NSAI-5681 — how a "connect this app" card ended. Every card reaches
// exactly one of these, and the record is what the four card styles render
// from — so the outcome survives a reload, matches on web and mobile, and does
// not have to be re-derived from timestamps on each client.
export enum EIntegrationConnectResolution {
  // NOTE: A connection for this app landed after the card was raised. The
  // server verifies this against the integrations table before recording it,
  // so a stale tab cannot mark a card connected.
  CONNECTED = "connected",
  // NOTE: A connect was started from the card and came back with nothing —
  // rejected consent, failed callback, abandoned provider tab. The only
  // resolution that still allows another attempt, until the next message.
  FAILED = "failed",
  // NOTE: The user closed the card.
  DISMISSED = "dismissed",
  // NOTE: The user moved on: a later message in the chat closed every card
  // still open (or failed) on the replies before it.
  TERMINATED = "terminated",
}

export interface IIntegrationConnectPrompt {
  provider: string;
  createdAt: string;
  // NOTE: Absent while the card is still open. The record is kept on the
  // message rather than removed, so the server's per-message dedupe still
  // recognises the app and the model cannot redraw a card the user answered.
  resolution?: EIntegrationConnectResolution;
  resolvedAt?: string;
  // NOTE: Recorded from the SERVER's integration row when the card resolves as
  // connected, so "Connected as mohd+1@nodeshift.com" stays true in the
  // transcript even after that account is later disconnected.
  connectedAccountTitle?: string;
}

export interface IResolveIntegrationConnectPromptPayload {
  chatUuid: string;
  messageUuid: string;
  provider: string;
  resolution: EIntegrationConnectResolution;
}

export interface IIntegrationConnectPromptPayload {
  uuid: string;
  prompt: IIntegrationConnectPrompt;
  chatUuid?: string;
  // NOTE: NSAI-5681 — the transcript line this resolution wrote, travelling
  // with the card it explains. A resolved card changes state and the chat gains
  // a line saying why; sending them separately meant the note only appeared on
  // the next history read (reported: "the note shows only after a refresh").
  // Absent when the outcome wrote no note — a card resolved silently because an
  // earlier one in the same chat already carried the line for this outcome.
  note?: IMessage;
}

export interface IMessageWithTempId extends IMessage {
  tempId?: string;
}

export interface IHasStatus {
  status: string;
}

export interface IChat {
  uuid: string;
  title: string;
  isArchived: boolean;
  isPinned: boolean;
  messages?: IMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface IChatTitleGenerated {
  chatUuid: string;
  title: string;
}

export interface IChatListResponse {
  data: IChatListObject[];
  hasNextPage: boolean;
}

export interface IFetchChatListProps {
  pageParam: number;
  searchQuery?: string;
}

export interface IUseChatListProps {
  searchQuery?: string;
}

export interface IChangedModelMessage {
  id: string;
  model: IModelForHistory;
  isOriginalModel: boolean;
  showResetModelButton: boolean;
}

export interface IChatSettings {
  isWebSearchEnabled: boolean;
  isDeepResearchEnabled: boolean;
  isPiiAnonymizationEnabled: boolean;
  // NOTE: Read-only flag the backend adds to the project chat-settings GET
  // response so the chat composer can show/hide the "Apply brand theme" toggle.
  // Optional because org-settings update payloads (web & mobile) reuse this
  // type without setting it.
  isPresentationThemeEnabled?: boolean;
  // NOTE: NSAI-5121 — the organization's brand palette, so a diagram drawn in
  // the transcript can be painted in the company's colours. Deliberately NOT
  // gated on the presentation builder the way `isPresentationThemeEnabled` is:
  // a diagram in a chat message is not a deck, so an org whose builder is off
  // should still get its own colours. Null when the org has no enabled theme,
  // and absent on the org-settings update payloads that reuse this type.
  diagramBrandPalette?: IPresentationThemePalette | null;
}

export interface IUpdateChatTitlePayload {
  uuid: string;
  title: string;
}

export interface IChatHistoryResponse {
  data: IMessage[];
  hasNextPage: boolean;
}

export interface IChatHistoryRequest {
  chatId: string;
  limit: number;
  offset: number;
}

// NOTE: chatUuid comes from the route parameter, not the body
export interface IBranchChatRequest {
  messageUuid: string;
  modelUuid?: string;
}

export interface IBranchChatResponse {
  chatUuid: string;
}

export interface IChatByUuidResponse {
  uuid: string;
  title: string;
  isArchived: boolean;
  // NOTE: NSAI-6473 — needed by the chat page's header, which offers the same
  // Pin / Unpin action the sidebar row does.
  isPinned: boolean;
  folderId: string | null;
  originalModels: IIndexedSelectionItemModel[];
  currentModels: IIndexedSelectionItemModel[];
  currentAssistants: IIndexedSelectionItemAssistant[];
  createdAt: string;
  updatedAt: string;
  searchMode: ESearchMode;
  parentChatUuid: string | null;
  parentChatTitle: string | null;
  branchedFromMessageUuid: string | null;
  telegramChatId: number | null;
  teamsConversationId: string | null;
  slackChannelId: string | null;
  // NOTE: Boolean flag only — the server deliberately never sends the WhatsApp
  // phone number on chat payloads (it is directly-identifying PII).
  isWhatsappChat: boolean;
  // NOTE: NSAI-4950 — boolean flag only; the Matrix room id stays server-side
  // and the clients only need to know the chat is read-only here.
  isMatrixChat: boolean;
}

export interface IUpdateChatCurrentModelParams {
  uuid: string;
  models?: IIndexedSelectionItem[];
  assistants?: IIndexedSelectionItem[];
}

export interface IChatHistoryPage {
  data: IMessage[];
  hasNextPage: boolean;
}

export interface IChatListPage {
  data: IChatListObject[];
  hasNextPage: boolean;
}

export interface IHasMessageUuidInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface IFindMessageInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface IUpdateMessageContentInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
  content: string;
}

export interface IUpdateMessageInPagesProps {
  pages: IChatHistoryPage[];
  message: IMessageWithTempId;
}

export interface IAppendMessageDeltaProps {
  pages: IChatHistoryPage[];
  uuid: string;
  delta: string;
}

export interface IMessageToolCall {
  name: string;
  args: Record<string, unknown>;
}

export interface ISetMessageToolCallProps {
  pages: IChatHistoryPage[];
  uuid: string;
  toolName: string;
  args: Record<string, unknown>;
}

export interface IResolveMessageToolCallProps {
  toolName: string;
  args: Record<string, unknown>;
  priorTool?: IMessageToolCall;
}

export interface IAppendMessageReasoningProps {
  pages: IChatHistoryPage[];
  uuid: string;
  reasoning: string;
}

export interface ISetMessageStreamErrorProps {
  pages: IChatHistoryPage[];
  uuid: string;
  errorMessage: string;
}

export interface ISetMessageStreamFinishedProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface ISetMessageDeepSearchProgressProps {
  pages: IChatHistoryPage[];
  uuid: string;
  progress: IMessage["deepSearchProgress"];
}

export interface ISetMessageWebSearchProgressProps {
  pages: IChatHistoryPage[];
  uuid: string;
  progress: IMessage["webSearchProgress"];
}

export interface ISetMessagePreparationProgressProps {
  pages: IChatHistoryPage[];
  uuid: string;
  progress: IMessage["preparationProgress"];
}

export interface ICancelSearchProgressForMessageInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface ICancelSearchProgressForChatInPagesProps {
  pages: IChatHistoryPage[];
}

export interface IUpsertNewMessagesIntoChatHistoryProps<TData> {
  oldData: TData | undefined;
  reversedMessages: IMessage[];
  lastMessageUuid: string;
}

export interface IApplyChatTitleGeneratedToChatListProps<TData = unknown> {
  oldData: TData;
  chat: IChatTitleGenerated;
}

// NOTE: NSAI-4601 — patches the cached chat-list entry's lastMessageAt when a
// new message arrives over the socket, so the sidebar re-sorts/re-groups
// (getChatActivityDate / groupChatsByDate) without waiting for a full list
// refetch. Mirrors IApplyChatTitleGeneratedToChatListProps.
export interface IApplyLastMessageAtToChatListProps<TData = unknown> {
  oldData: TData;
  chatUuid: string;
  lastMessageAt: string;
}

// NOTE: NSAI-4601 — the minimal shape applyLastMessageAtToChatList needs from
// its caller's cache wrapper. InfiniteData<IChatListPage> (the real TanStack
// Query type both web and mobile pass in) satisfies this structurally, so
// frontend-shared never has to import @tanstack/react-query to share the
// function itself — only its own `pages` field is ever read or spread.
export interface IChatListPagesContainer {
  pages: IChatListPage[];
}

// NOTE: NSAI-4601 (PR review follow-up, Sonar new-code duplication) — the
// minimal slice of TanStack Query's QueryClient that syncChatListLastMessageAt
// needs. The real QueryClient (from both web's and mobile's own
// @tanstack/react-query import) satisfies this structurally, so
// frontend-shared never has to depend on that package to share the call-site
// itself, which used to be hand-copied into both hooks' handleNewMessage.
export interface IChatListQueryClientLike {
  setQueriesData: <TData extends IChatListPagesContainer>(
    filters: { queryKey: readonly unknown[] },
    updater: (oldData: TData | undefined) => TData | undefined,
  ) => unknown;
}

export interface ISyncChatListLastMessageAtProps {
  queryClient: IChatListQueryClientLike;
  chatListQueryKey: readonly unknown[];
  messages: Array<{ createdAt: string; generationType?: EGenerationType }>;
  chatUuid: string;
}

// NOTE: NSAI-4601 — the platform-agnostic half of patching a single chat's
// fields inside a cached IChatListPage[]. Shared so web and mobile don't each
// carry their own copy of the "find by uuid, rebuild only the touched page,
// preserve reference equality when unchanged" traversal (the two apps still
// own their own InfiniteData<IChatListPage> wrapper, since that TanStack
// Query type isn't a frontend-shared dependency).
export interface IPatchChatListPagesProps<TPatch> {
  pages: IChatListPage[];
  chatUuid: string;
  patch: TPatch;
  // NOTE: Optional pre-check run against the matching chat item before it is
  // patched — return false to leave it untouched (e.g. a monotonic-timestamp
  // guard so a delayed/out-of-order event can't regress an already-newer
  // cached value). Defaults to always patching when the chat is found.
  shouldPatch?: (chatItem: IChatListObject) => boolean;
}

export interface IPatchChatListPagesResult {
  pages: IChatListPage[];
  wasUpdated: boolean;
}

// NOTE: NSAI-4686 — the counterpart of IPatchChatListPagesProps for a chat that
// is LEAVING the list (moved to a folder, archived, unarchived) rather than
// being patched in place. No `shouldPatch` guard: unlike a timestamp write there
// is no out-of-order event to defend against — the caller removes the chat
// because the user just dropped it somewhere else.
export interface IRemoveChatFromListPagesProps {
  pages: IChatListPage[];
  chatUuid: string;
}

export interface IRemoveChatFromChatListProps<TData = unknown> {
  oldData: TData;
  chatUuid: string;
}

// NOTE: NSAI-4686 (PR review) — where a chat sat before it was optimistically
// removed, so a failed mutation can put that one row back instead of restoring
// the whole cached list from a snapshot. A snapshot restore also reverts
// everything that landed while the request was in flight (a page the user
// scrolled in, another drop that already succeeded); a single-row re-insert
// leaves all of it alone.
export interface IChatListRemoval {
  pageIndex: number;
  itemIndex: number;
  chat: IChatListObject;
}

export interface IFindChatListRemovalProps {
  pages: IChatListPage[];
  chatUuid: string;
}

export interface IRestoreChatToListPagesProps {
  pages: IChatListPage[];
  removal: IChatListRemoval;
}

export interface IRestoreChatToChatListProps<TData = unknown> {
  oldData: TData;
  removal: IChatListRemoval;
}

// NOTE: NSAI-4686 (PR review) — one cached list's worth of rollback: the query
// the row was taken out of, and where it sat. The three drop mutations each
// collect a list of these in onMutate and hand it back in onError.
export interface IChatListRowRemoval {
  queryKey: readonly unknown[];
  removal: IChatListRemoval;
}

export interface ICollectChatListRemovalsProps<TData> {
  cachedChatLists: [readonly unknown[], TData | undefined][];
  chatUuid: string;
}

export interface IRestoreChatListRowsProps {
  queryClient: IChatListRollbackQueryClientLike;
  rows: IChatListRowRemoval[];
}

// NOTE: the slice of TanStack Query's QueryClient the rollback needs, for the
// same reason as IChatListQueryClientLike above: both apps' real client
// satisfies it structurally, so frontend-shared never imports the package.
export interface IChatListRollbackQueryClientLike {
  setQueryData: <TData extends IChatListPagesContainer>(
    queryKey: readonly unknown[],
    updater: (oldData: TData | undefined) => TData | undefined,
  ) => unknown;
}

// NOTE: NSAI-4792 — the chat list is paginated by OFFSET, so any mutation that
// reorders it (pin, archive, unarchive, move to/from a folder, delete) can shift
// a chat across a page boundary while the already-loaded pages are refetched.
// Until every page has settled, two of them can both report the same chat, and a
// list keyed by chat uuid then renders two children with the same key.
export interface IDedupeChatsByUuidProps {
  chats: readonly IChatListObject[];
}

// NOTE: NSAI-4601 — guards applyLastMessageAtToChatList against clobbering a
// newer cached value with a stale one. handleNewMessage patches the sidebar
// with lastIncomingMessage.createdAt, but that isn't guaranteed to be the
// chat's true chats.last_message_at in every case a "newMessages" socket
// event can fire: a multi-model turn saves its assistant rows concurrently
// (Promise.all preserves input order, not save-completion order), and some
// emitters (e.g. the multi-model summary placeholder) emit "newMessages" for
// a row that never went through the server's own touchChatLastMessageAt
// write at all. The server guards its column write the same way
// (`last_message_at < :next`) — this mirrors that guard client-side.
export interface IIsIncomingChatTimestampNewerProps {
  currentTimestamp?: string | null;
  incomingTimestamp: string;
}

// NOTE: NSAI-4601 — a "newMessages" batch is not guaranteed to be sorted by
// createdAt. The server builds it as [user, ...assistantMessagesForClient],
// and for a multi-model turn the assistant rows come from Promise.all in
// producer/request order, not DB save-completion order — so the array's last
// element is not reliably the one with the latest createdAt. Picking the
// true max avoids caching a lastMessageAt that undercounts the turn's real
// latest activity (flagged in PR review: web/mobile use-chat-web-socket.ts).
//
// NOTE: NSAI-4601 (PR review follow-up) — generationType is optional so the
// helper still works with a minimal test double, but every real IMessage
// carries it. Summary and BranchContext rows are excluded from the timestamp
// scan entirely: see GENERATION_TYPES_WITHOUT_LAST_MESSAGE_AT_BUMP in
// chat-list-cache.ts for why their createdAt must not be treated as the
// chat's new activity timestamp.
export interface IGetLatestMessageTimestampProps {
  messages: Array<{ createdAt: string; generationType?: EGenerationType }>;
}

// NOTE: NSAI-5681 — see appendMessageToChatHistory on each client: a resolved
// connect card's transcript line, put into the cached history where the next
// read would have returned it.
export interface IAppendMessageToChatHistoryProps {
  pages: IChatHistoryPage[];
  message: IMessage;
}

export interface IApplyIntegrationConnectPromptToMessageProps {
  pages: IChatHistoryPage[];
  uuid: string;
  prompt: IIntegrationConnectPrompt;
}

export interface IApplyToolApprovalRespondedToMessageProps {
  message: IMessage;
  payload: IToolApprovalRespondedPayload;
}

export interface IUpsertToolApprovalRequestToMessageProps {
  message: IMessage;
  payload: IToolApprovalRequestPayload;
}

export interface ISendToolApprovalParams {
  approvalId: string;
  approved: boolean;
  reason?: string;
}

export interface IMessageStoppedPayload {
  chatUuid: string;
}

export interface IMessagesDeletedPayload {
  chatUuid: string;
  count: number;
}

export interface IKnowledgeSourcesPayload {
  chatUuid: string;
  uuid: string;
  sources?: Array<{
    content: string;
    score: number;
    fileName: string;
    fileUuid: string;
    source?: EKnowledgeSourceType;
  }>;
  // NOTE: Cross-chat context provenance (NSAI-3501) — emitted alongside
  // knowledge sources when prior chats' user-memory facts fed this answer.
  contextChatSources?: IContextChatSource[];
}

export interface IWebSocketError {
  message: string;
  error?: string;
  // NOTE: NSAI-4636 — set to MODEL_UNAVAILABLE_ERROR_CODE by the server only when
  // a send failed because the selected model is unavailable (disabled/hidden at
  // any tier), so clients can invalidate the model-picker caches. Absent for
  // every other socket error.
  errorCode?: string;
  // NOTE: NSAI-5042 — set when the failing send targeted a known chat, so
  // clients only roll back optimistic messages in that chat.
  chatUuid?: string;
  // NOTE: NSAI-5641 — true when the server also wrote this error into the
  // assistant message, so it is already visible in the transcript. Clients must
  // then skip the toast: the same paragraph in two places at once is harder to
  // read, and a transcript entry persists where a toast auto-dismisses before a
  // full paragraph can be read. The event itself still arrives, because clients
  // rely on it to clear optimistic temp messages and restore the draft.
  isRenderedInTranscript?: boolean;
}

// NOTE: NSAI-5042 — reason a buffered/optimistic send ultimately failed.
export type TSendFailureReason =
  | "expired"
  | "ack-failed"
  | "auth-refresh-failed"
  // NOTE: NSAI-5042 — the server definitively rejected the turn post-ack (a
  // gateway "error" event stripped the optimistic temps); raised client-side
  // so the composer restores the draft for a send that never persisted.
  | "rejected"
  | "stalled";

// NOTE: NSAI-5042 — payload for the client-side send-failure channel
// (WebSocketService.onSendFailure). content carries the original user text so
// the UI can restore the draft instead of losing the input.
export interface ISendFailurePayload {
  chatId: string | null;
  content: string;
  reason: TSendFailureReason;
}

export interface IMessageFile {
  type: string;
  name?: string;
  // NOTE: S3 object key — set when file was uploaded via presigned URL.
  s3Key?: string;
  // NOTE: UUID of an existing message_files row. Used for resend — backend resolves to s3Key internally.
  fileUuid?: string;
}

export interface IMessagePayload {
  chatId?: string;
  content: string;
  role: EMessageRole;
  models?: IIndexedSelectionItem[];
  assistants?: IIndexedSelectionItem[];
  files?: IMessageFile[];
  searchMode: ESearchMode;
  requestId?: string;
  isRetry?: boolean;
  // NOTE: NSAI-3308 — the skills the user pinned to THIS message. Present on the
  // buffered payload as well as the outgoing one, so a reconnect replay pins the
  // same skills as the original send.
  skillIds?: string[];
  // NOTE: NSAI-5925 — the mentions the per-message cap refused. Buffered for the
  // same reason `skillIds` is: a replay must state the same refusals.
  refusedSkillNames?: string[];
}

// NOTE: React Native FormData accepts {uri, type, name} objects for file uploads
export interface IReactNativeFileUri {
  uri: string;
  type: string;
  name: string;
}

export interface ITranscribeAudioRequest {
  audio: Blob | IReactNativeFileUri;
  fileName?: string;
}

export interface ITranscribeAudioResponse {
  text: string;
}

export interface ISendMessagePayload {
  chatId?: string;
  content: string;
  role: EMessageRole;
  models?: IIndexedSelectionItem[];
  assistants?: IIndexedSelectionItem[];
  files?: IMessageFile[];
  searchMode: ESearchMode;
  reasoningMode?: EReasoningMode;
  // NOTE: Per-generation override for the org presentation brand theme. When
  // false, the brand theme is skipped for presentations generated this turn.
  applyPresentationTheme?: boolean;
  // NOTE: NSAI-3308 — stable skill uuids the user pinned to THIS message, never
  // skill instructions. The bound is a UI guard: the server re-authorizes every
  // uuid through the same resolver the model paths use.
  skillIds?: string[];
  // NOTE: NSAI-5925 — names of the skills this message mentioned and the cap
  // refused, so the sent message can record the refusal the composer showed. The
  // server normalizes them again; the gateway does not validate socket payloads.
  refusedSkillNames?: string[];
}

export interface IRequestMultiModelSummaryPayload {
  chatId: string;
  messageUuids: string[];
  // NOTE: NSAI-6445 — the member's OWN summary model, null when they have none.
  // Never fill it with a default: the server treats any uuid here as the
  // member's choice, so a client-side default hides the organization's Summary
  // model and the deployment default behind it.
  summaryModelUuid: string | null;
}

export interface IImprovePromptPayload {
  requestId: string;
  prompt: string;
  // NOTE: NSAI-6242 — uuid of the chat this draft belongs to, so the server can
  // resolve what the draft leaves implicit ("shorter", "the second point")
  // against the real conversation instead of rewriting it as if it were the
  // first message of a new chat. Optional because a draft in a chat that has not
  // been created yet has no uuid; that case improves with no context, as before.
  // The client sends the ID ONLY — the transcript is read server-side under the
  // caller's own user + project scope.
  chatId?: string;
}

export interface IPromptImproveDeltaPayload {
  requestId: string;
  delta: string;
}

export interface IPromptImproveFinishPayload {
  requestId: string;
  improvedPrompt: string;
}

export interface IPromptImproveErrorPayload {
  requestId: string;
  error: string;
  // NOTE: NSAI-2721 (review follow-up) — present only when the rejection is
  // a per-user rate-limit / cooldown bounce. The frontend uses this signal
  // to show a warning-style toast (rate limit is UX, not an outage) and to
  // disable the Improve button with a live countdown until the cap clears.
  // Absent / undefined for real errors (model failure, PII gateway down,
  // socket disconnect, etc.) — those still surface as error-style toasts.
  retryAfterSeconds?: number;
}

export interface IPresignFileUploadParams {
  fileName: string;
  contentType: string;
  // NOTE: NSAI-4264 — optional declared size, checked server-side as a cheap
  // early rejection before the S3 round trip. Not authoritative on its own.
  fileSizeBytes?: number;
  // NOTE: AbortSignal to cancel the presign request before upload starts
  signal?: AbortSignal;
}

export interface IPresignFileUploadResponse {
  uploadUrl: string;
  s3Key: string;
}

export interface IDownloadProgressEvent {
  loaded: number;
  total: number | undefined;
}

export interface IDownloadFileParams {
  fileUuid: string;
  onProgress?: (event: IDownloadProgressEvent) => void;
  // NOTE: NSAI-5312 — lets a one-file download started from a generated-files
  // selection be cancelled like the archive it stands in for.
  signal?: AbortSignal;
}

export interface IDownloadPreviewFileParams {
  fileUuid: string;
  onProgress?: (event: IDownloadProgressEvent) => void;
}

export interface IGetFilePreviewSlidesParams {
  fileUuid: string;
}

// NOTE: Per-slide preview images for a generated presentation file (NSAI-4418).
// A .pptx has no in-app renderer on React Native, so the server rasterizes the
// deck to images and mobile shows them as a swipeable slide gallery. Each entry
// is a ready-to-render image data URL (e.g. "data:image/jpeg;base64,…").
export interface IFilePreviewSlidesResponse {
  slides: string[];
  slideCount: number;
}

export interface IClearMessageStreamErrorParams {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface IPendingMessage {
  payload: IMessagePayload;
  timestamp: number;
  // NOTE: NSAI-5042 — flush retries already made for this entry; compared
  // against PENDING_MESSAGE_MAX_ATTEMPTS by decidePendingSend.
  attempts: number;
}

export interface IWebSocketServiceConfig {
  url: string;
  storageAdapter: IStorageAdapter;
  // NOTE: NSAI-4330 — when set, read on every `connect()` instead of `url`, so
  // a deployment the user switched to at runtime (mobile "Change tenant") is
  // honoured by the next socket without recreating the singleton.
  getUrl?: () => string;
  getLanguage?: () => string | null;
  getProjectUuid?: () => string | null;
  onTokenRefresh?: () => Promise<string | null>;
  // NOTE: Optional getter for E2E test scenario header
  getTestScenario?: () => string | null;
  // NOTE: NSAI-5909 — applies whatever the platform's stream buffer has parked.
  // Per-token frames are coalesced client-side, so every OTHER socket event
  // must see them applied first: a `streamFinish` or `messageUpdated` that
  // overtook a parked delta would be undone when the delta finally landed.
  // Called here rather than from each consumer because `onAny` listeners run
  // ahead of every named listener, which is the only place that ordering can
  // be guaranteed for the chat hook and the background trackers alike.
  onFlushStreamBuffer?: () => void;
  // NOTE: NSAI-5117 — true while an assistant reply is actively streaming for
  // any chat this client cares about. Supplying it turns on the deferred auth
  // reconnect: a token refresh mid-stream updates `socket.auth` but does NOT
  // cycle the handshake until the stream settles, so the in-flight (often
  // still-empty) assistant message is not dropped. Omitted ⇒ reconnect
  // immediately, which is the behaviour of a client that never defers.
  hasActiveStream?: () => boolean;
  // NOTE: NSAI-5117 — lets a platform keep its own request-id generator. The
  // web client uses `crypto.randomUUID()`; omitted ⇒ the built-in generator.
  generateRequestId?: () => string;
  // NOTE: NSAI-5117 — runs before a send is emitted and may throw to abort it.
  // Exists for the web E2E hook that forces a synchronous send failure.
  beforeSendMessage?: () => void;
  // NOTE: NSAI-5117 — E2E-only timing compression. Each returns undefined
  // outside a test run, so production always uses the real constants.
  getAckTimeoutMsOverride?: () => number | undefined;
  getHealthCheckTimeoutMsOverride?: () => number | undefined;
  // NOTE: NSAI-5117 — the gateway rejected a guarded event because the licence
  // expired. The web client stores a return path and navigates to its
  // licence-expired route; a client that omits this just logs the exception.
  onLicenseExpired?: () => void;
}

export interface IClearMessageStreamErrorProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

export interface IPendingFile {
  type: string;
  name?: string;
  s3Key?: string;
  // NOTE: UUID of an existing file. Used for resend — backend resolves to s3Key internally.
  fileUuid?: string;
}

export interface IModelMeta {
  title: string;
  provider?: string;
}

export interface IGetModelByIdParams {
  models: IModel[];
  id: string;
  index?: number;
}

export interface IGetAssistantByIdParams {
  assistants: IAssistant[];
  id: string;
  index?: number;
}

export interface IReplaceSelectedModelParams {
  selectedModels: IIndexedSelectionItemModel[];
  models: IModel[];
  // NOTE: Null when the picker has no replace target; the helper then no-ops.
  targetUuid: string | null;
  nextUuid: string;
}

export interface IReplaceSelectedAssistantParams {
  selectedAssistants: IIndexedSelectionItemAssistant[];
  assistants: IAssistant[];
  // NOTE: Null when the picker has no replace target; the helper then no-ops.
  targetUuid: string | null;
  nextUuid: string;
}

export interface IBaseModelOrAssistantForUpdateChat {
  uuid: string;
  index: number;
}

export interface IGetTruncatedDisplayNameParams {
  text: string;
  maxLength: number;
}

export interface IGetTruncatedDisplayNameResult {
  displayName: string;
  isTruncated: boolean;
}

interface IPayloadFile {
  name: string;
  type: string;
}

export interface IGetApproxSocketPayloadSizeBytesProps {
  messageContent: string;
  files: IPayloadFile[];
  extraOverheadBytes?: number;
  perFileOverheadBytes?: number;
}

export enum EMicState {
  Idle = "idle",
  Recording = "recording",
  Transcribing = "transcribing",
}

export enum EAudioTranscriptionDisclaimerDecision {
  Agreed = "agreed",
  Cancelled = "cancelled",
}

export enum EBlockKind {
  ModelChange = "modelChange",
  Single = "single",
  Row = "row",
  Disclaimer = "disclaimer",
}

export type TBlock =
  | {
      kind: EBlockKind.ModelChange;
      key: string;
      models: IModelForHistory[];
      previousModels: IModelForHistory[];
      modelUuids: string[];
      isOriginalModelSet: boolean;
      showResetModelButton: boolean;
    }
  | { kind: EBlockKind.Single; key: string; message: IMessage }
  | {
      kind: EBlockKind.Row;
      key: string;
      userMessage: IMessage;
      children: IMessage[];
    }
  // NOTE: NSAI-5978 — a synthetic row carrying the AI disclaimer, inserted once
  // after a chat's first AI answer. Mobile only: web keeps its caption under the
  // composer, where a single line costs nothing, so its own block builder never
  // emits this kind.
  | { kind: EBlockKind.Disclaimer; key: string };

export interface ICollectSummarySourceUuidsProps {
  messages: IMessage[];
  assistantMessageIndex: number;
}

export interface IFindLastRetryableSummaryUuidProps {
  renderedBlocks: TBlock[];
}

export interface IGetSummaryButtonVisibilityProps {
  blocksAfter: TBlock[];
  children: IMessage[];
  chatId?: string;
}

export interface IGetSummaryButtonVisibilityResult {
  shouldShow: boolean;
  areAllChildrenSettled: boolean;
  summarizableMessageUuids: string[];
}

// NOTE: Cross-chat semantic search request — `q` mirrors the server-side
// DTO field name (`@Query()` reads `q` from the URL). Limit is the page
// size cap; both fields are optional from the FE side but the API enforces
// a length check on `q`.
export interface ISearchAcrossChatsParams {
  q: string;
  limit?: number;
}

// NOTE: A single semantic search hit shared between the web and mobile
// clients. `matchedAt` arrives as an ISO string from the JSON wire, but
// React Query consumers re-parse to Date downstream; keeping it as string
// here matches the actual payload bytes. `projectName` is the owning
// project's title (cross-chat search is org-scoped, so a hit can belong
// to a project other than the active one); null when the project row
// could not be hydrated server-side (concurrent soft-delete).
export interface ICrossChatSearchHit {
  chatUuid: string;
  chatTitle: string | null;
  projectName: string | null;
  messageUuid: string;
  snippet: string;
  score: number;
  matchedAt: string;
}

export type TCrossChatSearchResponse = ICrossChatSearchHit[];

// NOTE: NSAI-5908 — the structural slice of TanStack's
// InfiniteData<IChatHistoryPage> that the copy-on-write cache helpers need.
// Same reasoning as IChatListPagesContainer above: frontend-shared never
// imports @tanstack/react-query, and both apps' real InfiniteData satisfies
// this shape structurally.
export interface IChatHistoryPagesContainer {
  pages: IChatHistoryPage[];
}

export interface IMessageLocationInPages {
  pageIndex: number;
  messageIndex: number;
}

export interface IFindMessageLocationInPagesProps {
  pages: IChatHistoryPage[];
  uuid: string;
}

// NOTE: NSAI-5908 — what a message mutator receives from
// updateMessageInChatHistory: the cloned message it may mutate in place, and
// the rebuilt pages array that already holds that clone, so the existing
// `pages`-based helpers (appendMessageDelta, setMessageToolCall, …) keep
// working unchanged — they find the clone by uuid and mutate it.
export interface IMutateChatHistoryMessageProps {
  message: IMessage;
  pages: IChatHistoryPage[];
}

export interface IUpdateMessageInChatHistoryProps<
  TData extends IChatHistoryPagesContainer,
> {
  oldData: TData;
  uuid: string;
  // NOTE: Platform clone for ONE message — web passes structuredClone, mobile
  // its deepClone. A deep copy (not a spread) so a mutator that pushes into a
  // nested array can never write through into the cached original.
  cloneMessage: (message: IMessage) => IMessage;
  // NOTE: Returns whether anything changed; `false` hands the untouched
  // `oldData` back so observers are not notified for a no-op.
  mutate: (props: IMutateChatHistoryMessageProps) => boolean;
}

export interface IUpdateFirstPageInChatHistoryProps<
  TData extends IChatHistoryPagesContainer,
> {
  oldData: TData;
  // NOTE: Returns the next `data` array for the first page, or `null` to
  // leave the cache untouched (same reference out).
  update: (data: IMessage[]) => IMessage[] | null;
}

export interface IMapMessagesInChatHistoryProps<
  TData extends IChatHistoryPagesContainer,
> {
  oldData: TData;
  // NOTE: Returns a replacement message, or `null` to keep the original
  // reference. Only pages holding a replaced message are rebuilt.
  map: (message: IMessage) => IMessage | null;
}

// NOTE: NSAI-5908 — text and reasoning streamed for one message since the
// last flush. Deltas are concatenated in arrival order, so applying the chunk
// is indistinguishable from applying every frame one by one.
export interface IPendingMessageStreamChunk {
  // NOTE: Deltas that arrived after the latest full replacement (or since the
  // last drain when there was none), in arrival order.
  content: string;
  reasoning: string;
  // NOTE: The text of the latest `messageContentUpdate` — a full replacement
  // that supersedes every delta parked before it.
  replacementContent?: string;
}

// NOTE: chatUuid → (message uuid → pending chunk).
export type TPendingStreamChunkBuffer = Map<
  string,
  Map<string, IPendingMessageStreamChunk>
>;

export interface IEnqueueStreamChunkProps {
  buffer: TPendingStreamChunkBuffer;
  chatUuid: string;
  uuid: string;
  delta?: string;
  reasoning?: string;
  replacementContent?: string;
}

export interface IDrainStreamChunksProps {
  buffer: TPendingStreamChunkBuffer;
  // NOTE: Omit to drain every chat.
  chatUuid?: string;
}

export interface IDrainedStreamChunks {
  chatUuid: string;
  chunks: Map<string, IPendingMessageStreamChunk>;
}

export interface IHasPendingStreamChunksProps {
  buffer: TPendingStreamChunkBuffer;
  chatUuid?: string;
}
